import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  IconButton,
  Modal,
  Box,
  Button,
} from "@mui/material";
import { FaCheck, FaTimes } from "react-icons/fa";
import "./AgentWithdrawlRequests.css";
import { axiosInstance } from "../../axiosInstance";
import { toast } from "react-toastify";
import { GoSearch } from "react-icons/go";

// Table columns
const columns = [
  { id: "withdraw_id", label: "WITHDRAW ID", minWidth: 120, align: "center" },
  { id: "agent_name", label: "AGENT NAME", minWidth: 150, align: "center" },
  {
    id: "points_to_withdraw",
    label: "WITHDRAW POINTS",
    minWidth: 150,
    align: "center",
  },
  {
    id: "created_at",
    label: "REQUESTED DATE",
    minWidth: 150,
    align: "center",
  },
  {
    id: "payment_status",
    label: "PAYMENT STATUS",
    minWidth: 150,
    align: "center",
  },
  {
    id: "updated_at",
    label: "APPROVED DATE",
    minWidth: 150,
    align: "center",
  },
  { id: "actions", label: "ACTIONS", minWidth: 150, align: "center" },
];

const formatDate = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  if (isNaN(date)) return "--";
  return date.toLocaleDateString("en-GB");
};

export function AgentWithdrawlRequests() {
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const [withdrawList, setWithdrawList] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAgentWithdrawlRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/admin/agent-withdraw/");
      console.log("✅ Withdraw list response:", res.data);
      if (res.data.status === "success") {
        setWithdrawList(res.data.data || []);
      } else {
        setError("Failed to fetch withdraw requests.");
      }
    } catch (err) {
      console.error("❌ Error fetching withdraw requests:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentWithdrawlRequests();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleActionClick = (row, type) => {
    setSelectedRow(row);
    setModalType(type);
    setModalOpen(true);
  };

  // Confirm action
  const handleConfirmAction = async () => {
    if (!selectedRow) return;
    try {
      const res = await axiosInstance.patch(
        `/admin/agent-withdraw/${selectedRow.withdraw_id}/`,
        {
          payment_status: modalType === "accept" ? "success" : "failed",
        }
      );

      if (res.data.status === "success") {
        toast.success("Withdraw request updated successfully");
        fetchAgentWithdrawlRequests();
        setModalOpen(false);
      } else {
        toast.error("Failed to update withdraw request");
      }
    } catch (err) {
      console.error("❌ Error updating withdraw request:", err);
      toast.error("Error updating withdraw request");
    }
  };

  // ✅ Search by agent name OR approved date (updated_at)
  const filteredData = withdrawList.filter((row) => {
    const agentName = `${row.agent?.first_name || ""} ${
      row.agent?.last_name || ""
    }`.toLowerCase();
    const approvedDate = formatDate(row.updated_at).toLowerCase();
    return (
      agentName.includes(searchQuery.toLowerCase()) ||
      approvedDate.includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div>
      <div className="admin-top-container">
        <div className="admin-listText">Withdraw Requests</div>
        {/* <div>
          <select
            className="listAdmin-showRecords"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
          >
            <option value={20}>Show No of Records</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
            <option value={2000}>2000</option>
            <option value={5000}>5000</option>
          </select>
        </div> */}
        <div className="search-container">
          <div className="nav-search">
            <span className="search-icons">
              <GoSearch />
            </span>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ border: "none", outline: "none", marginLeft: "6px" }}
            />
          </div>
        </div>
      </div>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 440 }} className="Table-container">
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    className="table-header"
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredData.length > 0 ? (
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow
                      className="table-row"
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.withdraw_id}
                      sx={{
                        backgroundColor: index % 2 === 1 ? "#F9FAFB" : "white",
                      }}
                    >
                      <TableCell align="center">{row.withdraw_id}</TableCell>
                      <TableCell align="center">
                        {row.agent?.first_name} {row.agent?.last_name}
                      </TableCell>
                      <TableCell align="center">
                        {row.points_to_withdraw}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(row.created_at)}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={`status-text ${row.payment_status.toLowerCase()}`}
                      >
                        {row.payment_status}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(row.updated_at)}
                      </TableCell>
                      <TableCell align="center">
                        {row.payment_status.toLowerCase() === "pending" ? (
                          <div className="action-buttons">
                            <IconButton
                              className="accept-btn"
                              onClick={() => handleActionClick(row, "accept")}
                            >
                              <FaCheck />
                            </IconButton>
                            <IconButton
                              className="reject-btn"
                              onClick={() => handleActionClick(row, "reject")}
                            >
                              <FaTimes />
                            </IconButton>
                          </div>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    style={{ padding: "40px 0" }}
                  >
                    No Data Available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <h3 id="toggle-status-title">Are you sure?</h3>
          <hr />
          <h6>
            {modalType === "accept"
              ? `Accept withdraw request for ${selectedRow?.agent?.first_name} ${selectedRow?.agent?.last_name}?`
              : `Reject withdraw request for ${selectedRow?.agent?.first_name} ${selectedRow?.agent?.last_name}?`}
          </h6>
          <hr />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button onClick={() => setModalOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              color={modalType === "accept" ? "success" : "error"}
            >
              Confirm
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
